/**
 * Reset Password Page
 * Allows users to set a new password using a reset token
 */

import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link as MuiLink,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { authApi } from "@/api/auth";
import { getPasswordError, passwordFieldDescribedBy } from "@/utils/passwordRules";
import { PasswordRequirements } from "@/components/common/PasswordRequirements";
import { TenantBranding } from "@/components/common/TenantBranding";
import { usePublicTenant } from "@/hooks/useTenants";

interface ResetPasswordForm {
  newPassword: string;
  confirmPassword: string;
}

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const tenantId = searchParams.get("tenant");

  // Fetch tenant info for branding (if tenant ID is in URL)
  const { data: tenant, isLoading: tenantLoading } = usePublicTenant(tenantId || "");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordForm>();

  const newPassword = watch("newPassword");

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!token) {
      setError("Invalid or missing reset token. Please request a new password reset.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await authApi.confirmPasswordReset({
        token,
        new_password: data.newPassword,
      });

      setSuccess(true);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; error?: { message?: string } } } };
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        "Failed to reset password. The link may be expired.";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Box
              sx={{
                textAlign: "center",
                mb: 3
              }}>
              <TenantBranding
                tenantName={tenant?.name}
                isLoading={tenantLoading}
                size="medium"
              />
            </Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              Invalid or missing reset token. Please request a new password reset.
            </Alert>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate("/forgot-password")}
            >
              Request Password Reset
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{
              textAlign: "center"
            }}>
              <Box sx={{
                mb: 3
              }}>
                <TenantBranding
                  tenantName={tenant?.name}
                  isLoading={tenantLoading}
                  size="medium"
                />
              </Box>
              <CheckCircleIcon sx={{ fontSize: 60, color: "success.main", mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Password Reset Successful!
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  marginBottom: "16px"
                }}>
                Your password has been reset successfully. Redirecting to login...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box
            sx={{
              textAlign: "center",
              mb: 3
            }}>
            <Box sx={{
              mb: 2
            }}>
              <TenantBranding
                tenantName={tenant?.name}
                isLoading={tenantLoading}
                size="medium"
              />
            </Box>
            <Typography variant="h4" gutterBottom sx={{
              fontWeight: 700
            }}>
              Set Your Password
            </Typography>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Create a password to access your account
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              fullWidth
              type={showPassword ? "text" : "password"}
              label="New Password"
              id="reset-new-password"
              margin="normal"
              {...register("newPassword", {
                required: "Password is required",
                // Same rules and messages as the server.
                validate: (value) => getPasswordError(value) ?? true,
              })}
              error={!!errors.newPassword}
              helperText={errors.newPassword?.message}
              slotProps={{
                htmlInput: {
                  "aria-describedby": passwordFieldDescribedBy(
                    "reset-new-password",
                    "reset-password-requirements",
                    !!errors.newPassword,
                  ),
                },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
            />
            <PasswordRequirements
              id="reset-password-requirements"
              password={newPassword ?? ""}
            />

            <TextField
              fullWidth
              type={showConfirmPassword ? "text" : "password"}
              label="Confirm New Password"
              margin="normal"
              {...register("confirmPassword", {
                required: "Please confirm your password",
                validate: (value) =>
                  value === newPassword || "Passwords do not match",
              })}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      >
                        {showConfirmPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isSubmitting}
              sx={{ mt: 3, mb: 2 }}
            >
              {isSubmitting ? "Setting Password..." : "Set Password"}
            </Button>

            <Box
              sx={{
                textAlign: "center",
                mt: 2
              }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Already have a password?{" "}
                <MuiLink
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate("/login")}
                  sx={{ cursor: "pointer" }}
                >
                  Back to Login
                </MuiLink>
              </Typography>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ResetPasswordPage;
